import React, { useEffect, useRef } from "react";

import { z } from "zod";

import { Icon } from "@braze/beacon-core";

import useI18nNamespace from "@services/useI18nNamespace";
import { useRegisterChangeTool } from "@services/useRegisterChangeTool";

import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay";

import type { UseClearToolParams } from "./types";

/**
 * Hook to register a clear tool for operator.
 * Shared between BrazeSelect, BrazeSelectAsync, and BrazeSelectAsyncCreatable.
 */
export const useClearTool = <OptionType,>({
  id,
  isDisabled,
  operatorDisabledReason,
  operatorRequiresApproval = "yes",
  isClearable,
  isMulti,
  operatorLabel,
  valueRef,
  onChangeRef,
  formatOptionStringLabel,
}: UseClearToolParams<OptionType>) => {
  const { t } = useI18nNamespace("braze-select");
  const registerChangeTool = useRegisterChangeTool({ operatorDisabledReason });

  const formatOptionStringLabelRef = useRef(formatOptionStringLabel);
  formatOptionStringLabelRef.current = formatOptionStringLabel;

  useEffect(() => {
    if (isDisabled || !isClearable) {
      return;
    }

    const cleanup = registerChangeTool({
      toolEntityId: id,
      name: "clear_selection",
      description:
        "Clear every selected value from the matching dropdown. This tool takes no arguments; identify the target and its current selection from read_current_page before calling.",
      parameters: z.object({}),
      callback: () => {
        const onChangeHandler = onChangeRef.current;
        if (!onChangeHandler) {
          throw new Error(
            t("clear-selection-error-no-on-change-handler", "This dropdown cannot be cleared programmatically.")
          );
        }

        const currentValue = valueRef.current;
        const removedValues: OptionType[] = Array.isArray(currentValue)
          ? [...currentValue]
          : currentValue !== null && currentValue !== undefined
            ? [currentValue as OptionType]
            : [];

        const clearedValue = isMulti ? [] : null;
        onChangeHandler(clearedValue, {
          action: "clear",
          removedValues,
        });

        return t("clear-selection-success-message", "Selection was successfully cleared.");
      },
      options: {
        requiredSkill: "page-controls",
        toolGroup: "Agentic Creation & Editing",
        labels: {
          ready: t("clear-selection-ready", "Preparing to clear"),
          running: t("clear-selection-running", "Clearing"),
          complete: t("clear-selection-complete", "Cleared"),
          failed: t("clear-selection-failed", "Failed to clear"),
          stopped: t("clear-selection-stopped", "Stopped clearing"),
        },
        requiresApproval: operatorRequiresApproval,
        argumentsSummaryText: () => operatorLabel,
        renderApproval: () => {
          const raw = valueRef.current;
          const currentLabel =
            raw === null || raw === undefined
              ? ""
              : Array.isArray(raw)
                ? raw.map((v) => formatOptionStringLabelRef.current(v)).join(", ")
                : formatOptionStringLabelRef.current(raw as OptionType);
          return <DiffDisplay title={operatorLabel} original={currentLabel} modified="" />;
        },
        toolIcon: <Icon name="times-circle" />,
      },
    });

    return cleanup;
  }, [
    isDisabled,
    isClearable,
    registerChangeTool,
    operatorRequiresApproval,
    id,
    t,
    isMulti,
    operatorLabel,
    valueRef,
    onChangeRef,
  ]);
};
